import React, { FC } from 'react'

import { alpha, Box, useTheme } from '@mui/material'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { chartOptions } from '../chartOptions'
import { EmptyData } from '../EmptyData'
import { Indicators } from '../Indicators'
import { ReportWidgetsHeader } from '../ReportWidgetsHeader'
import { StyledWidgetWrapper } from '../StyledWidgetWrapper'
import { StressLevelType } from '../types'

import { getLocalizedDurationLabel } from './getLocalizedDurationLabel'

export const StressLevelCorrelationWidget: FC<{
  stressLevelData: StressLevelType[] | undefined
  displayWidth: number
}> = ({ stressLevelData, displayWidth }) => {
  const { t } = useTranslation()
  const { spacing, palette } = useTheme()

  const chartData = {
    labels: stressLevelData?.map((item) => getLocalizedDurationLabel(item.duration, t)),
    datasets: [
      {
        label: t('positive', 'Positive'),
        data: stressLevelData?.map((item) => item.positive),
        backgroundColor: palette.green[400],
        barThickness: 16,
        borderRadius: 4,
        borderColor: palette.primary.light,
        borderWidth: 2,
        hoverBackgroundColor: alpha(palette.green[400] as string, 0.8),
      },
      {
        label: t('negative', 'Negative'),
        data: stressLevelData?.map((item) => item.negative),
        backgroundColor: palette.red[400],
        barThickness: 16,
        borderRadius: 4,
        borderColor: palette.primary.light,
        borderWidth: 2,
        hoverBackgroundColor: alpha(palette.red[400] as string, 0.8),
      },
    ],
  }

  const isDataEmpty = stressLevelData?.every(
    ({ positive, negative }) => positive === 0 && negative === 0,
  )

  return (
    <StyledWidgetWrapper>
      <ReportWidgetsHeader
        text={t('stressLevelCorrelation', 'Stress level correlation')}
        explanation={t(
          'theGraphRepresentsHowManyTransactionsNumberGotPositiveOrNegativeStressAssessmentsInCategory',
          'The graph represents how many transactions number-wise got positive or negative stress assessments in each category',
        )}
      >
        <Indicators
          positiveLabel={t('positive', 'Positive')}
          negativeLabel={t('negative', 'Negative')}
        />
      </ReportWidgetsHeader>

      {isDataEmpty || isDataEmpty === undefined ? (
        <Box mt={4}>
          <EmptyData
            title={t('notEnoughData', 'Not enough data')}
            subtitle={t('pleaseCompleteJournalingSection', 'Please complete "Journaling" section')}
          />
        </Box>
      ) : (
        <Box ml={spacing(-3)}>
          <Bar
            data={chartData}
            options={chartOptions()}
            style={{ maxHeight: spacing(31) }}
            key={displayWidth}
          />
        </Box>
      )}
    </StyledWidgetWrapper>
  )
}
